import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import CoverProduct from '@/components/CoverProduct'
import Hero from '@/components/Hero'
import TextBlock from '@/components/TextBlock'
import ProductTable from '@/components/ProductTable'
import DownloadTable from '@/components/DownloadTable'
import Carousel from '@/components/Carousel'
import Footer from '@/components/Footer'
import RevealGrid from '@/components/RevealGrid'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { cutExcerpt } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const Product = ({
  cpt: cptFromProps,
  stub: stubFromProps,
}) => {
  const { getImageUrl, headerHeight, setPageAnimationReady, setHeaderInverse } = useContext(GeneralContext)
  const classes = useStyles({ headerHeight })
  const preload = useRef(new CreateJs.LoadQueue())
  const { stub: stubFromParams } = useParams()
  const stub = useRef(stubFromParams || stubFromProps)
  const [load, setLoad] = useState(false)
  const $root = useRef()
  const gallery = useRef([])

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    strings: state.language.string,
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getSingleProduct = useCallback((cpt, index) => dispatch(actions.page.getDataCPT(cpt, index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub.current, 'pages')
    preload.current.loadManifest(heroMedia)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = () => {
    if (page.project_gallery_id) {
      const gal = page.project_gallery_id.split(',')
      const imgSize = 'full'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub.current, 'pages')
          const ratio = height / width

          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      gallery.current = galleryItems[0] ? galleryItems : []
    }
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
      setGallery()
    }
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    getSingleProduct(cptFromProps, stub.current)
  }

  const getProductTableData = () => {
    const items = []
    if (page.product_barrier) items.push({ label: strings.barrier_label, value: page.product_barrier })
    if (page.product_length) items.push({ label: strings.length_label, value: page.product_length })
    if (page.product_asi) items.push({ label: strings.asi_label, value: page.product_asi })
    if (page.product_class) items.push({ label: strings.class_label, value: page.product_class })
    if (page.product_width) items.push({ label: strings.width_label, value: page.product_width })
    if (page.product_deflession) items.push({ label: strings.deflession_label, value: page.product_deflession })
    if (page.product_height) items.push({ label: strings.height_label, value: page.product_height })
    if (page.product_basis) items.push({ label: strings.basis_label, value: page.product_basis })
    if (page.product_weight) items.push({ label: strings.weight_label, value: page.product_weight })
    if (page.product_test) items.push({ label: strings.test_label, value: page.product_test })
    if (page.product_anchored) items.push({ label: strings.anchored_label, value: page.product_anchored })
    if (page.product_terminal) items.push({ label: strings.terminal_label, value: page.product_terminal })
    if (page.product_certification) items.push({ label: strings.certification_label, value: page.product_certification })
    if (page.product_table_addons.length) page.product_table_addons.map((addon) => items.push({ label: addon.label, value: addon.value }))
    return items
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Cover Product
  ------------------------------*/
  const renderCover = () => {
    return (
      <CoverProduct
        title={page.title.rendered}
        denomination={page.product_denomination}
        subtitle={page.product_subtitle}
        mainImg={getImageUrl(page.featured_media, 'large', stub.current)}
        altImg={getImageUrl(page.product_secondary_img, 'large', stub.current)}
        onComplete={() => setPageAnimationReady(true)}
      />
    )
  }

  /*------------------------------
  Render Text
  ------------------------------*/
  const renderText = () => {
    return page.content.rendered && (
      <div className={`${classes.wrapper} ${classes.description}`}>
        <TextBlock text={page.content.rendered} />
      </div>
    )
  }

  /*------------------------------
  Render Product Table
  ------------------------------*/
  const renderProductTable = () => {
    return getProductTableData().length > 0 && (
      <div className={`${classes.wrapper} ${classes.productTable}`}>
        <ProductTable
          title={page._cta_category_product.includes(10) ? strings.table_title_2 : strings.table_title} // eslint-disable-line
          items={getProductTableData()}
        />
      </div>
    )
  }

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGalleryProduct = () => {
    return gallery.current.length > 1 && (
      <div className={classes.galleryWrapper}>
        <RevealGrid
          cols={1}
          className={classes.gallery}
        >
          <Carousel
            imagesList={gallery.current}
          />
        </RevealGrid>
      </div>
    )
  }

  /*------------------------------
  Render Gallery Related
  ------------------------------*/
  const renderGalleryRelated = () => {
    return (
      page.related_project.length > 0
      && page.related_project?.[0]?.gallery.length > 0
      && !getProductTableData().length
    ) && (
      <RevealGrid
        className={classes.gallery}
        cols={1}
      >
        <Carousel
          imagesList={page.related_project[0].gallery.map((img) => (
            {
              src: img.full[0],
              width: img.full[1],
              height: img.full[2],
            }))}
        />
      </RevealGrid>
    )
  }

  /*------------------------------
  Render Download Table
  ------------------------------*/
  const renderDownloadTable = () => {
    return page.file_item.length > 0 && (
      <div className={`${classes.wrapper} ${classes.download}`}>
        <DownloadTable
          title={strings.download_title}
          items={page.file_item}
        />
      </div>
    )
  }

  /*------------------------------
  Render Related Projects
  ------------------------------*/
  const renderRelatedProject = () => {
    return page.related_project.length > 0 && (
      <div className={classes.hero}>
        {page.related_project.map((item) => (
          <Hero
            key={item.ID}
            borderTitle
            fontSize="sm"
            img={item.cover.full[0]}
            link={item.link}
            pretitle={strings.case_studies_label}
            text={cutExcerpt(item.excerpt)}
            title={`${item.title}<br />${item.location}`}
          />
        ))}
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCover()}
        {renderText()}
        {renderProductTable()}
        {gallery.current.length > 0 ? renderGalleryProduct() : renderGalleryRelated()}
        {renderDownloadTable()}
        {renderRelatedProject()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageProduct ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Product
